import { Box, EmptyState, FlexBox, Text } from '@dixa/lib-shared-habla';
import { KnowledgeLoading } from 'lib-knowledge-utils/components/loading';

import { useSuggestionComparison } from '../hooks/useSuggestionComparison';
import { Suggestion } from '../utils/toSuggestion';

import s from './articleComparison.module.scss';

/**
 * An update draft beside the article it would replace. The draft leads on the
 * left, as version history also orders its panels.
 */
export function ArticleComparison({
  suggestion,
}: {
  suggestion: Suggestion;
}): JSX.Element {
  const { newMarked, oldMarked, loading, unavailable } =
    useSuggestionComparison(suggestion);

  return (
    <>
      {loading && <KnowledgeLoading />}

      {!loading && unavailable && (
        <EmptyState
          size="small"
          icon="exclamation-triangle-filled"
          title="Nothing to compare against"
          description="The article this draft updates could not be loaded. It may have been deleted since the draft was written."
        />
      )}

      {!loading && !unavailable && (
        <>
          {/* Outside the scroller, so the columns stay labelled however far
              down the articles the reviewer has read. */}
          <FlexBox flex="0 0 auto" className={s.labels}>
            <Text.Paragraph variant="4" subdued width="50%" padding="300 400">
              Updated Version
            </Text.Paragraph>
            <Text.Paragraph variant="4" subdued width="50%" padding="300 400">
              Previous Version
            </Text.Paragraph>
          </FlexBox>

          <Box flex="1" minHeight="0" className={s.scroll}>
            <FlexBox
              flexDirection="row"
              alignItems="stretch"
              minHeight="100%"
              className={s.panels}
            >
              <div className={s.panel}>
                {/* Both sides are generated markup — the draft's from the AI,
                    the article's from `serializeToHtml`. Rendered the same way
                    version history renders its own diff panels. */}
                <div
                  className={s.panelArticle}
                  dangerouslySetInnerHTML={{ __html: newMarked }}
                />
              </div>

              <div className={s.panel}>
                <div
                  className={s.panelArticle}
                  dangerouslySetInnerHTML={{ __html: oldMarked }}
                />
              </div>
            </FlexBox>
          </Box>
        </>
      )}
    </>
  );
}
